import type { FormGroupProps } from '@ksyos/design-system/dist/components/form-group';
import type { RadioGroupProps } from '@ksyos/design-system/dist/components/radio-group';
import { FormGroup } from '@ksyos/design-system';
import type { FormContextProps } from '@frontend/components/elements/formContext';
import type { OmittedFormGroupFieldProps } from '@frontend/components/fields/FormGroup';
import { FormContextConsumer } from '@frontend/components/elements/formContext';
import { computeFormGroupProps } from '@frontend/components/fields/FormGroup';
import { getValidationErrorTranslation } from '@frontend/utils/index';
import type { BooleanRadioGroupProps } from './BooleanRadioGroup';
import { BooleanRadioGroup } from './BooleanRadioGroup';

type OmittedBooleanRadioGroupProps = Omit<BooleanRadioGroupProps, 'onChange'>;

export interface BooleanRadioGroupFieldProps extends OmittedBooleanRadioGroupProps {
    automation?: string;
    isClearable?: boolean;
    onChange: (value: boolean | null) => void | Promise<void>;
    onDoValidate?: () => Promise<void>;
}

export function computeBooleanRadioGroupProps(properties: BooleanRadioGroupFieldProps): BooleanRadioGroupProps {
    return {
        automation: properties.automation,
        error: getValidationErrorTranslation(properties.error),
        onChange: async (value) => {
            if (properties.onChange) {
                await properties.onChange(value);
            }
            if (properties.onDoValidate) {
                await properties.onDoValidate();
            }
        },
        onClearValue: properties.isClearable
            ? async () => {
                  await properties.onChange(null);
              }
            : undefined,
        labelFalse: properties.labelFalse,
        labelTrue: properties.labelTrue,
        layout: properties.layout,
        name: properties.name,
        type: properties.type,
        value: properties.value,
    };
}

type BooleanRadioGroupFormGroupFieldPropsBase = OmittedFormGroupFieldProps & BooleanRadioGroupFieldProps;

export interface BooleanRadioGroupFormGroupFieldProps extends BooleanRadioGroupFormGroupFieldPropsBase {
    formGroupLayout?: FormGroupProps['layout'];
    radioGroupLayout?: RadioGroupProps['layout'];
}

export function BooleanRadioGroupFormGroupField(props: FormContextProps<BooleanRadioGroupFormGroupFieldProps>) {
    return (
        <FormContextConsumer {...props}>
            {(properties) => (
                <FormGroup
                    layout={props.formGroupLayout}
                    {...computeFormGroupProps(properties)}
                >
                    <BooleanRadioGroup
                        layout={props.radioGroupLayout}
                        {...computeBooleanRadioGroupProps(properties)}
                    />
                </FormGroup>
            )}
        </FormContextConsumer>
    );
}
